import { useMemo, useState } from 'react'
import { Button, Card, DatePicker, Input, Space, Statistic } from 'antd'
import {
  ReloadOutlined,
  ThunderboltOutlined,
  WarningOutlined,
  TeamOutlined,
  AppstoreOutlined,
  PercentageOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined
} from '@ant-design/icons'
import dayjs, { type Dayjs } from 'dayjs'
import { AppLayout } from '../../../components/AppLayout'
import { Loading } from '../../../components/Loading'
import { EmptyState } from '../../../components/EmptyState'
import { useBilling } from '../hooks/useBilling'
import { BillingUsageTable } from '../components/BillingUsageTable'
import { ManualTriggerModal } from '../components/ManualTriggerModal'
import { useAuth } from '../../../auth/AuthProvider'
import { isBillingAdmin } from '../../../utils/roles'

export function BillingPage() {
  const { user } = useAuth()
  const canAdminister = isBillingAdmin(user)
  const [selectedMonth, setSelectedMonth] = useState<Dayjs | null>(dayjs())
  const [tenantFilter, setTenantFilter] = useState('')
  const [triggerOpen, setTriggerOpen] = useState(false)

  const period = selectedMonth ? selectedMonth.format('YYYYMM') : ''

  // Fetch the whole period once; filter by tenant code in memory so typing doesn't refetch.
  const { items, loading, error, refresh } = useBilling(period)

  const filteredItems = useMemo(() => {
    const query = tenantFilter.trim().toLowerCase()
    if (!query) return items
    return items.filter(i => i.tenantCode.toLowerCase().includes(query))
  }, [items, tenantFilter])

  // Stat cards reflect the visible (filtered) rows; computed once per items/filter change.
  const stats = useMemo(
    () => ({
      totalBillable: filteredItems.reduce((sum, i) => sum + i.billableHeadcount, 0),
      totalPurchased: filteredItems.reduce((sum, i) => sum + i.purchasedLicenses, 0),
      totalExcess: filteredItems.reduce((sum, i) => sum + i.excessUsage, 0),
      workspaceCount: new Set(filteredItems.map(i => i.tenantCode)).size,
      tenantsWithExcess: new Set(filteredItems.filter(i => i.excessUsage > 0).map(i => i.tenantCode)).size,
      finalisedCount: filteredItems.filter(i => i.status === 'Finalised').length,
      totalEntries: filteredItems.length
    }),
    [filteredItems]
  )

  return (
    <AppLayout>
      <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 112px)' }}>
        <div
          style={{
            marginBottom: 16,
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            flexShrink: 0
          }}
        >
          <Space size="middle" wrap>
            <DatePicker
              picker="month"
              value={selectedMonth}
              onChange={val => setSelectedMonth(val)}
              format="YYYY-MM"
              allowClear={false}
            />
            <Input
              placeholder="Filter by tenant code..."
              value={tenantFilter}
              onChange={e => setTenantFilter(e.target.value)}
              style={{ width: 220 }}
              allowClear
            />
            <Button icon={<ReloadOutlined />} onClick={refresh} loading={loading}>
              Refresh
            </Button>
            {canAdminister && (
              <Button type="primary" icon={<ThunderboltOutlined />} onClick={() => setTriggerOpen(true)}>
                Run calculation…
              </Button>
            )}
          </Space>
        </div>

        <div style={{ marginBottom: 16, display: 'flex', gap: 16, flexShrink: 0 }}>
          <Card size="small" loading={loading} style={{ flex: 1 }}>
            <Statistic title="Workspaces" value={stats.workspaceCount} prefix={<AppstoreOutlined />} />
          </Card>
          <Card size="small" loading={loading} style={{ flex: 1 }}>
            <Statistic title="Total Billable" value={stats.totalBillable} prefix={<TeamOutlined />} />
          </Card>
          <Card size="small" loading={loading} style={{ flex: 1 }}>
            <Statistic
              title="Utilization"
              value={stats.totalPurchased > 0 ? Math.round((stats.totalBillable / stats.totalPurchased) * 100) : 0}
              suffix="%"
              prefix={<PercentageOutlined />}
            />
          </Card>
          <Card size="small" loading={loading} style={{ flex: 1 }}>
            <Statistic
              title="Total Excess"
              value={stats.totalExcess}
              prefix={<WarningOutlined />}
              valueStyle={stats.totalExcess > 0 ? { color: '#cf1322' } : {}}
            />
          </Card>
          <Card size="small" loading={loading} style={{ flex: 1 }}>
            <Statistic
              title="Tenants w/ Excess"
              value={stats.tenantsWithExcess}
              prefix={<ExclamationCircleOutlined />}
              valueStyle={stats.tenantsWithExcess > 0 ? { color: '#cf1322' } : {}}
            />
          </Card>
          <Card size="small" loading={loading} style={{ flex: 1 }}>
            <Statistic
              title="Finalised"
              value={`${stats.finalisedCount} / ${stats.totalEntries}`}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </div>

        <div style={{ flex: 1, minHeight: 0 }}>
          {loading && items.length === 0 ? (
            <Loading />
          ) : error ? (
            <EmptyState description={error.message} />
          ) : !period ? (
            <EmptyState description="Select a period to view billing usage" />
          ) : filteredItems.length === 0 ? (
            <EmptyState description="No billable headcount recorded for this period" />
          ) : (
            <BillingUsageTable items={filteredItems} loading={loading} />
          )}
        </div>
      </div>

      <ManualTriggerModal
        open={triggerOpen}
        defaultMonth={selectedMonth}
        onClose={() => setTriggerOpen(false)}
        onComplete={refresh}
      />
    </AppLayout>
  )
}
